import {FC, useEffect, useMemo, useState} from 'react';
import {toast} from 'react-toastify';
//mui
import {Box, Tab, Tabs, Typography} from '@mui/material';
//formik
import {Formik} from 'formik';
import {AuctionFormValues, initialValues} from '../AuctionItem/formConfiguration';
//context
import {AuctionDetailsContext, AuctionDetailsContextType} from './AuctionDetailsContext';
//services
import {OrganizationAuctionService} from '@/utils/services';
//constants
import {BIDS, H5, ITEM_DETAILS} from '@/utils/constants';
//types
import {IOrganizationAuctionResponse} from '@/utils/types';
//helpers
import {AuctionDetailsTabsType} from './helpers';
import {getEditableAuctionInitialValues, setUpdatedAuctionValues} from '../AuctionItem/helpers';
import {getErrorMessage} from '@/utils/helpers';
//components
import AuctionDetailsItem from './AuctionDetailsItem';
import AuctionDetailsBids from './AuctionDetailsBids';
//styles
import {auctionDetailsStyle} from './AuctionDetails.styles';
import {useSelector} from 'react-redux';
import {getAuctionSelector} from '@/store/organization-auction';
import {useApiInstance} from '@/contexts/ApiInstanceContext';

interface AuctionDetailsProps {
  onCloseModal: () => void;
  editableAuction: IOrganizationAuctionResponse | null;
  handleProfileTabStyles?: (isProfilePage: boolean) => void;
}

const AuctionDetails: FC<AuctionDetailsProps> = ({onCloseModal, editableAuction, handleProfileTabStyles}) => {
  const auctionData = useSelector(getAuctionSelector);

  const {apiInstance} = useApiInstance();

  const handleSubmitClickHandler = async (values: AuctionFormValues) => {
    try {
      const reqBody = setUpdatedAuctionValues(values);
      await OrganizationAuctionService.updateAuction(editableAuction!.id, reqBody);
      onCloseModal();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const auctionFormInitialValues = useMemo(() => {
    if (editableAuction) {
      const editableAuctionInitialValues = getEditableAuctionInitialValues(editableAuction);
      return editableAuctionInitialValues;
    }
    return initialValues;
  }, [editableAuction]);

  const auctionDetailsTabs: AuctionDetailsTabsType[] = [
    {title: ITEM_DETAILS, content: <AuctionDetailsItem />},
    {title: BIDS, content: <AuctionDetailsBids />},
  ];

  const [activeTab, setActiveTab] = useState<AuctionDetailsTabsType>(auctionDetailsTabs[0]);

  const changeTab = (newTab: AuctionDetailsTabsType) => () => setActiveTab(newTab);

  const auctionDetailsContextValue: AuctionDetailsContextType = useMemo(() => {
    return {
      editableAuction,
      onCloseModal,
    };
  }, [editableAuction, onCloseModal]);

  useEffect(() => {
    if (!editableAuction) return;
    const getAuctionData = async () => {
      await OrganizationAuctionService.getAuction(editableAuction?.id, apiInstance);
    };

    getAuctionData();
  }, [editableAuction, activeTab]);

  useEffect(() => {
    if (activeTab.title === ITEM_DETAILS && handleProfileTabStyles) {
      handleProfileTabStyles(true);
    }
    return () => {
      if (handleProfileTabStyles) {
        handleProfileTabStyles(false);
      }
    };
  }, [activeTab, handleProfileTabStyles]);

  return (
    <AuctionDetailsContext.Provider value={auctionDetailsContextValue}>
      <Box sx={auctionDetailsStyle.wrapper}>
        <Typography sx={auctionDetailsStyle.title} variant={H5}>
          {auctionData?.name}
        </Typography>
        <Tabs value={activeTab.title} sx={auctionDetailsStyle.tabs}>
          {auctionDetailsTabs.map((tab: AuctionDetailsTabsType) => (
            <Tab
              onClick={changeTab(tab)}
              key={tab.title}
              value={tab.title}
              label={<Box>{tab.title}</Box>}
              sx={auctionDetailsStyle.tab}
              disableRipple
            />
          ))}
        </Tabs>

        <Formik initialValues={auctionFormInitialValues} onSubmit={handleSubmitClickHandler}>
          {activeTab.content}
        </Formik>
      </Box>
    </AuctionDetailsContext.Provider>
  );
};

export default AuctionDetails;
